<template>

  <div class="einvoice-timeline-shell">

    <div class="timeline-summary">

      <div class="timeline-summary__eyebrow">Tiến trình hóa đơn điện tử</div>

      <div class="timeline-summary__title"> {{ currentStepMeta.label }} </div>

      <div class="timeline-summary__caption">{{ currentStepMeta.caption }}</div>

    </div>

    <div class="einvoice-timeline">

      <div
        v-for="(step, idx) in timelineSteps"
        :key="idx"
        class="timeline-step"
        :class="{
          'is-active': step.isActive,
          'is-completed': step.isCompleted,
          'is-error': step.isError,
        }"
      >

        <div class="timeline-marker">
           <i
            v-if="step.isCompleted"
            class="fas fa-check"
          ></i
          > <i
            v-else-if="step.isError"
            class="fas fa-times"
          ></i
          > <i
            v-else-if="step.isActive"
            class="fas fa-circle fa-xs"
          ></i
          > <span
            v-else
            class="marker-dot"
          ></span
          >
        </div>

        <div
          v-if="idx < timelineSteps.length - 1"
          class="timeline-line"
        ></div>

        <div class="timeline-content">
           <span class="timeline-label font-weight-bold">{{ step.label }}</span
          > <span
            v-if="step.date"
            class="timeline-date text-muted"
            > {{ step.date }} </span
          >
        </div>

      </div>

    </div>

  </div>

</template>

<script>
  import moment from 'moment'
  import { EINVOICE_STATUS, EINVOICE_STATUS_NAME } from '@/utils/einvoice.enum'

  const STEP_ORDER = [
    { key: EINVOICE_STATUS.DRAFT, label: 'Nháp GTGT' },
    {
      key: EINVOICE_STATUS.READY_TO_ISSUE,
      label: 'Sẵn sàng phát hành',
    },
    { key: EINVOICE_STATUS.ISSUING, label: 'Đang phát hành' },
    { key: EINVOICE_STATUS.ISSUED, label: 'Đã phát hành' },
    { key: EINVOICE_STATUS.TAX_PENDING, label: 'Đang chờ CQT' },
    {
      key: EINVOICE_STATUS.TAX_ACCEPTED,
      label: 'CQT chấp nhận',
    },
  ]

  export default {
    name: 'EInvoiceStatusTimeline',
    props: {
      invoice: {
        type: Object,
        required: true,
      },
    },
    computed: {
      currentStepMeta() {
        const currentStatus = this.invoice.status
        const label = EINVOICE_STATUS_NAME[currentStatus] || 'Đang xử lý'

        if (currentStatus === EINVOICE_STATUS.DRAFT) {
          return {
            label: 'Draft GTGT đã được tạo',
            caption: 'Bạn có thể tiếp tục rà soát dữ liệu và phát hành khi đủ điều kiện.',
          }
        }

        if (currentStatus === EINVOICE_STATUS.READY_TO_ISSUE) {
          return {
            label: 'Draft đã đủ điều kiện phát hành',
            caption: 'Thông tin bắt buộc đã ổn. Bước tiếp theo là phát hành hóa đơn.',
          }
        }

        if (currentStatus === EINVOICE_STATUS.ISSUE_FAILED) {
          return {
            label: 'Phát hành lỗi',
            caption: 'Cần đọc lỗi cụ thể trước khi retry để tránh lặp thao tác sai.',
          }
        }

        if (currentStatus === EINVOICE_STATUS.TAX_REJECTED) {
          return {
            label: 'CQT từ chối',
            caption: 'Hóa đơn cần được xử lý theo phản hồi từ cơ quan thuế.',
          }
        }

        return {
          label,
          caption: 'Theo dõi timeline để biết bản ghi đang ở pha nào của flow HĐĐT.',
        }
      },
      timelineSteps() {
        const currentStatus = this.invoice.status
        const isRejected = currentStatus === EINVOICE_STATUS.TAX_REJECTED
        const isFailed = currentStatus === EINVOICE_STATUS.ISSUE_FAILED
        const isCanceled = currentStatus === EINVOICE_STATUS.CANCELED

        let steps = STEP_ORDER.map((step) => {
          const isCompleted = currentStatus > step.key
          const isActive = currentStatus === step.key
          return {
            label: step.label,
            isCompleted,
            isActive,
            isError: false,
            date: this.getStepDate(step.key),
          }
        })

        // Add terminal states if applicable
        if (isRejected) {
          steps.push({
            label: 'CQT từ chối',
            isCompleted: false,
            isActive: true,
            isError: true,
            date: this.formatDate(this.invoice.taxCheckedAt),
          })
        }

        if (isFailed) {
          steps.push({
            label: EINVOICE_STATUS_NAME[EINVOICE_STATUS.ISSUE_FAILED],
            isCompleted: false,
            isActive: true,
            isError: true,
            date: this.formatDate(this.invoice.updatedAt || this.invoice.issuedAt),
          })
        }

        if (isCanceled) {
          steps.push({
            label: 'Đã huỷ',
            isCompleted: false,
            isActive: true,
            isError: true,
            date: this.formatDate(this.invoice.canceledAt),
          })
        }

        return steps
      },
    },
    methods: {
      formatDate(dateStr) {
        if (!dateStr) return ''
        return moment(dateStr).format('DD/MM/YYYY HH:mm')
      },
      getStepDate(statusKey) {
        const inv = this.invoice
        const dateMap = {
          [EINVOICE_STATUS.DRAFT]: inv.createdAt || inv.updatedAt,
          [EINVOICE_STATUS.READY_TO_ISSUE]: inv.updatedAt || inv.createdAt,
          [EINVOICE_STATUS.ISSUING]: inv.updatedAt || inv.issuedAt,
          [EINVOICE_STATUS.ISSUED]: inv.issuedAt,
          [EINVOICE_STATUS.TAX_PENDING]: inv.issuedAt,
          [EINVOICE_STATUS.TAX_ACCEPTED]: inv.taxCheckedAt,
        }
        return this.formatDate(dateMap[statusKey])
      },
    },
  }
</script>

<style lang="scss" scoped>
.einvoice-timeline {
  display: flex;
  align-items: flex-start;
  gap: 0;
  padding: 0.85rem 0 0.2rem;
  overflow-x: auto;
}

.einvoice-timeline-shell {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.timeline-summary {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.timeline-summary__eyebrow {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6f7d8f;
}

.timeline-summary__title {
  font-size: 1rem;
  font-weight: 700;
  color: #2f3b49;
}

.timeline-summary__caption {
  font-size: 0.82rem;
  color: #7d8795;
  line-height: 1.5;
}

.timeline-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  flex: 1;
  min-width: 0;

  .timeline-marker {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #e7ebf1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.7rem;
    color: #fff;
    z-index: 1;
    transition: all 0.25s ease;

    .marker-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #aeb8c6;
      transition: background 0.2s ease;
    }
  }

  .timeline-line {
    position: absolute;
    top: 14px;
    left: 50%;
    width: 100%;
    height: 2px;
    background: #e6ebf1;
    z-index: 0;
  }

  .timeline-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 0.5rem;
    text-align: center;
  }

  .timeline-label {
    font-size: 0.75rem;
    color: #778497;
    font-weight: 500;
    line-height: 1.35;
  }

  .timeline-date {
    font-size: 0.68rem;
    margin-top: 2px;
    color: #8c8c8c;
  }

  &.is-completed {
    .timeline-marker {
      background: #27b3ab;
    }
    .timeline-line {
      background: #27b3ab;
    }
    .timeline-label {
      color: #27b3ab;
    }
    i {
      color: white
    }
  }

  &.is-active {
    .timeline-marker {
      background: #3699ff;
      box-shadow: 0 0 0 4px rgba(54, 153, 255, 0.14);
    }
    .timeline-label {
      color: #3699ff;
    }
  }

  &.is-error {
    .timeline-marker {
      background: #f64e60;
      box-shadow: 0 0 0 4px rgba(246, 78, 96, 0.14);
    }
    .timeline-label {
      color: #f64e60;
    }
  }
}

@media (max-width: 767.98px) {
  .timeline-step {
    min-width: 110px;
  }
}
</style>
